import React, { useEffect, useState } from 'react';
import {
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TablePagination,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Typography,
  Button,
  Checkbox,
  Collapse,
  Grid,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import Iconify from '../common/iconify.component';
import Sortable from './sortable.component';
import NoData from './no-data.component';
import Error from './error.component';
import Loading from './loading.component';
import CellValue from './cell-value.component';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import ChannelVideosCountInfo from '../form-elements/channel-videos-count-info.component';

const ListData = ({
  columns,
  rows,
  page,
  rowsPerPage,
  totalRows,
  loading,
  actions,
  error,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onAction,
  forReports,
  handleToggleHeaderFooter,
  handleSecondaryGenreClick,
  showCheckbox,
  selectedRows,
  setSelectedRows,
  selectAllPages,
  setSelectAllPages,
  subRowColumns,
  handleShowSubRow,
  handleMigratedClick,
  disabledSwitch,
  paginationAction,
  onPaginationActionChange,
}) => {
  const colSpan = columns.length + (actions.length ? 1 : 0);

  const [anchorActionEl, setAnchorActionEl] = useState(null);
  const [openActions, setOpenActions] = useState({
    show: false,
    data: null,
  });
  const [clickedRowData, setClickedRowData] = useState({});

  const handleActionOpen = (e, selectedRow) => {
    setAnchorActionEl(e.currentTarget);
    setOpenActions({
      ...openActions,
      show: true,
      data: selectedRow,
    });
  };
  const handleActionClose = () => {
    setAnchorActionEl(null);
    setOpenActions({
      ...openActions,
      show: false,
      data: null,
    });
  };
  const handleActionSelect = (selectedAction) => {
    onAction(selectedAction, openActions.data);
    handleActionClose();
  };
  const handleSingleActionSelect = (data) => {
    onAction(actions[0].action, data);
    handleActionClose();
  };

  const isPageSelected =
    rows.length > 0 && rows.every((row) => selectedRows.includes(row.id));

  const handleRowSelection = (id) => {
    const finalSelectedRows = selectedRows.includes(id)
      ? selectedRows.filter((rowId) => rowId !== id)
      : [...selectedRows, id];

    setSelectedRows(finalSelectedRows);
    if (finalSelectedRows.length < rowsPerPage) {
      setSelectAllPages(false);
    }
  };

  const handlePageSelection = () => {
    const pageRowIds = rows.map((row) => row.id);
    if (isPageSelected) {
      setSelectedRows((prevSelected) =>
        prevSelected.filter((id) => !pageRowIds.includes(id))
      );
    } else {
      setSelectedRows((prevSelected) => [
        ...new Set([...prevSelected, ...pageRowIds]),
      ]);
    }
  };

  useEffect(() => {
    if (showCheckbox) {
      if (selectAllPages) {
        setSelectedRows(rows.map((row) => row.id));
      }
    }
  }, [selectAllPages, rows]);

  return (
    <>
      <Grid container spacing={1} display="flex" alignItems="center">
        {paginationAction && (
          <Grid item xs={12} sm={4} md={4}>
            <ChannelVideosCountInfo
              id="channel_videos_count_info"
              name="channel_videos_count_info"
              label=""
              defaultValue={paginationAction}
              onChange={(e) => onPaginationActionChange(e.target.value)}
              sx={{ width: '100%', m: 1 }}
            />
          </Grid>
        )}

        {totalRows > 0 && (
          <Grid
            item
            xs={12}
            sm={paginationAction ? 8 : 12}
            md={paginationAction ? 8 : 12}
          >
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(e, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                onRowsPerPageChange(Number(e.target.value));
              }}
            />
          </Grid>
        )}
      </Grid>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {showCheckbox && !loading && !error && rows.length !== 0 && (
                <TableCell align="center" width="2%">
                  <Checkbox
                    checked={isPageSelected}
                    onChange={handlePageSelection}
                  />
                </TableCell>
              )}

              {actions.length > 0 && (
                <TableCell align="center" width="5%">
                  Action
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell
                  key={`col-${c.id}`}
                  align={c.align}
                  width={c.width}
                  sx={{
                    cursor: c.sortable ? 'pointer' : 'inherit',
                    padding: forReports ? '5px !important' : '16px',
                    fontSize: forReports
                      ? '0.750rem !important'
                      : '0.875rem !important',
                  }}
                  onClick={() => {
                    if (c.sortable) {
                      if (c.dataKey === sortBy) {
                        if (sortOrder === '') {
                          onSortChange(c.dataKey, 'asc');
                        } else if (sortOrder === 'asc') {
                          onSortChange(c.dataKey, 'desc');
                        } else if (sortOrder === 'desc') {
                          onSortChange('', '');
                        }
                      } else {
                        onSortChange(c.dataKey, 'asc');
                      }
                    }
                  }}
                >
                  <Stack direction="row" justifyContent={c.align}>
                    {c.sortable && (
                      <Sortable
                        dataKey={c.dataKey}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                      />
                    )}
                    {c.label}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && !error && (
              <>
                {rows.map((r, index) => (
                  <>
                    <TableRow key={`row-${r[columns[0].dataKey]}-${index}`}>
                      {showCheckbox && !loading && !error && (
                        <TableCell align="center" width="2%">
                          <Checkbox
                            checked={selectedRows.includes(r.id)}
                            onChange={() => {
                              handleRowSelection(r.id);
                            }}
                          />
                        </TableCell>
                      )}

                      {actions.length > 1 && (
                        <TableCell align="center" width="5%">
                          <IconButton
                            size="small"
                            color="inherit"
                            onClick={(e) => handleActionOpen(e, r)}
                          >
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      )}

                      {actions.length === 1 && (
                        <TableCell align="right" width="5%">
                          {!actions[0].conditional ||
                          actions[0].conditional(r) ? (
                            <Button
                              size="small"
                              color="info"
                              variant="contained"
                              onClick={() => handleSingleActionSelect(r)}
                            >
                              {actions[0].label}
                            </Button>
                          ) : (
                            ''
                          )}
                        </TableCell>
                      )}
                      {columns.map((c) => (
                        <TableCell
                          key={`data-col-${c.id}-${r[c.dataKey]}`}
                          align={c.align}
                          width={c.width}
                          sx={{
                            padding: forReports ? '5px !important' : '16px',
                            fontSize: forReports
                              ? '0.700rem !important'
                              : '0.875rem !important',
                          }}
                          variant={
                            forReports && index === 0 ? 'head' : 'string'
                          }
                        >
                          <CellValue
                            type={c.type}
                            dataKey={c.dataKey}
                            value={r[c.dataKey]}
                            fallbackValue={(e) => {
                              if (
                                (c.dataKey === 'showInHeader' ||
                                  c.dataKey === 'showInFooter') &&
                                c.type === CELL_DATA_TYPES.SWITCH
                              ) {
                                handleToggleHeaderFooter(
                                  r,
                                  e.target.checked,
                                  c.dataKey === 'showInHeader'
                                );
                              } else if (
                                c.dataKey === 'secondaryGenres' &&
                                c.type === CELL_DATA_TYPES.SECONDARY_GENRES
                              ) {
                                handleSecondaryGenreClick({
                                  ...e,
                                  parentId: r.id,
                                });
                              } else if (
                                (c.dataKey === 'migrated' ||
                                  c.dataKey === 'status') &&
                                c.type === CELL_DATA_TYPES.SWITCH
                              ) {
                                handleMigratedClick(r, e.target.checked);
                              } else if (c.dataKey === 'isActive') {
                                handleMigratedClick(r, e.target.checked);
                              }
                            }}
                            expandSubRow={r.subRows?.length > 0}
                            handleShowSubRow={() => handleShowSubRow(r)}
                            isWebsiteLink={
                              c.dataKey === 'websiteUrl' &&
                              c.type === CELL_DATA_TYPES.LINK
                            }
                            rowData={r}
                            clickedRowData={clickedRowData}
                            handleShowTooltip={() => {
                              if (r?.id === clickedRowData?.id) {
                                setClickedRowData({});
                              } else {
                                setClickedRowData(r);
                              }
                            }}
                            disabledSwitch={disabledSwitch}
                          />

                          {c.secondaryFields && (
                            <>
                              {c.secondaryFields.map((sf, sfIdx) => (
                                <React.Fragment
                                  key={`sf-data-col-${c.id}-${sfIdx}`}
                                >
                                  <br />
                                  <CellValue
                                    type={sf.type}
                                    dataKey={sf.dataKey}
                                    value={r[sf.dataKey]}
                                    color="secondary"
                                  />
                                </React.Fragment>
                              ))}
                            </>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>

                    {!r?.subRowsLoading &&
                      r?.subRows?.length > 0 &&
                      r.subRows.map((sr) => (
                        <TableRow
                          sx={{
                            backgroundColor: '#ebebeb',
                            boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.2)',
                          }}
                        >
                          {actions.length > 1 && <TableCell />}
                          <TableCell />
                          {subRowColumns.map((src) => (
                            <TableCell
                              key={`data-col-${sr.id}-${src.id}`}
                              align={src.align}
                              width={src.width}
                              sx={{ paddingY: '2px' }}
                              colSpan={columns.length - 2}
                            >
                              <Collapse in timeout="auto" unmountOnExit>
                                <CellValue
                                  type={src.type}
                                  dataKey={src.dataKey}
                                  value={sr[src.dataKey]}
                                />
                              </Collapse>
                            </TableCell>
                          ))}
                          <TableCell />
                        </TableRow>
                      ))}
                  </>
                ))}
              </>
            )}

            {!loading && !error && totalRows === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <NoData />
                </TableCell>
              </TableRow>
            )}

            {!loading && error && totalRows === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <Error />
                </TableCell>
              </TableRow>
            )}

            {loading && (
              <TableRow>
                <TableCell align="center" colSpan={colSpan}>
                  <Loading />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalRows > 0 && (
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            onRowsPerPageChange(Number(e.target.value));
          }}
        />
      )}

      {openActions.show && (
        <Menu
          id="actions-menu"
          anchorEl={anchorActionEl}
          open
          onClose={() => handleActionClose()}
          size="small"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {actions.map((a, index) => {
            let render = true;
            if (a.conditional) {
              render = a.conditional(openActions.data);
            }

            if (render) {
              return a.link ? (
                <MenuItem
                  key={`mia-${a.action}-${index}`}
                  component={RouterLink}
                  to={a.link?.replace(':id', openActions.data.id)}
                  disabled={
                    a.conditionalDisabled &&
                    a.conditionalDisabled(openActions.data)
                  }
                >
                  <Typography variant="body2">{a.label}</Typography>
                </MenuItem>
              ) : (
                <MenuItem
                  key={`mia-${a.action}-${index}`}
                  onClick={() => handleActionSelect(a.action)}
                  disabled={
                    a.conditionalDisabled &&
                    a.conditionalDisabled(openActions.data)
                  }
                >
                  <Typography variant="body2">{a.label}</Typography>
                </MenuItem>
              );
            }

            return null;
          })}
        </Menu>
      )}
    </>
  );
};

ListData.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  error: PropTypes.bool.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  forReports: PropTypes.bool,
  handleToggleHeaderFooter: PropTypes.func,
  handleSecondaryGenreClick: PropTypes.func,
  showCheckbox: PropTypes.bool,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func,
  selectAllPages: PropTypes.bool,
  setSelectAllPages: PropTypes.func,
  subRowColumns: PropTypes.arrayOf(PropTypes.shape()),
  handleShowSubRow: PropTypes.func,
  handleMigratedClick: PropTypes.func,
  disabledSwitch: PropTypes.bool,
  paginationAction: PropTypes.string,
  onPaginationActionChange: PropTypes.func,
};
ListData.defaultProps = {
  onAction: () => {},
  forReports: false,
  handleToggleHeaderFooter: () => {},
  handleSecondaryGenreClick: () => {},
  showCheckbox: false,
  selectedRows: [],
  setSelectedRows: () => {},
  selectAllPages: false,
  setSelectAllPages: () => {},
  subRowColumns: [],
  handleShowSubRow: () => {},
  handleMigratedClick: () => {},
  disabledSwitch: false,
  paginationAction: '',
  onPaginationActionChange: () => {},
};

export default ListData;
